/**
 * El enum de aliados del producto — la ÚNICA lista de slugs válidos.
 *
 * Tres consumidores la amarran, y por eso vive en el model (data pura, sin
 * dependencias) en vez de derivarse de cualquiera de ellos:
 *  - `ALLY_CATALOG` (screens) se declara `Record<AllyId, Ally>`: un aliado sin
 *    entrada de catálogo —o una entrada sin id— truena el compile.
 *  - `validateFlow` exige que las choices de un nodo `declaresAlly` sean ids de
 *    aquí: un slug con typo en el grid ya no cae en silencio al aliado de
 *    fallback del catálogo, truena en CI.
 *  - `ONBOARDING_TARGETS` (el deep-link `?partner=`) genera un target por id:
 *    dar de alta un aliado le crea su URL de campaña sin tocar rutas.
 */
export const ALLY_IDS = [
	// Admattic es una red de AFILIADOS, no una plataforma con la que el usuario
	// trabaje: entra aquí porque la plomería de `?partner=` (captura en el boot,
	// TTL, prestamp, estampado en el record) es exactamente la que la campaña
	// necesita. A propósito NO está en las choices del nodo `ally_grid` del flow
	// — solo se alcanza por su URL de campaña. Ver designs/alianza-admattic.
	'admattic',
	'digitalhub',
	// Calcu mx es una CALCULADORA de impuestos, no una plataforma de trabajo: el
	// usuario llega desde el anuncio que aparece al terminar su cálculo. Entra
	// por la misma razón que Admattic y Digital Hub —la plomería de `?partner=`
	// es la que la campaña necesita— y con la misma consecuencia: fuera del grid,
	// solo alcanzable por su URL. Lo que la distingue es que su anuncio SABE el
	// régimen del usuario (lo acaba de calcular) y lo manda en `?calc=`.
	'calcumx',
	'tual',
	// Maslow es una app de beneficios para empleados de empresas: el usuario no
	// trabaja con ella. Está en el grid, como Calcu.mx, y su link (`/maslow` o
	// `?partner=maslow`) entra directo a la alianza.
	'maslow',
	'ag_enterprises',
	'airbnb',
	'boosmap',
	'deel',
	'didi',
	'didi_food',
	'doctoralia',
	'kovi',
	'lalamove',
	'mercado_envios',
	'mercado_libre',
	'morada_uno',
	'plata',
	'rappi',
	'terapify',
	'uber',
	'uber_eats',
	'walmart',
	'workana',
	'zubale',
] as const;

export type AllyId = (typeof ALLY_IDS)[number];

const ALLY_ID_SET = new Set<string>(ALLY_IDS);

export const isAllyId = (value: string | null | undefined): value is AllyId =>
	!!value && ALLY_ID_SET.has(value);
